import { z } from 'zod'

export const MEMBERSHIP_ME_READ_CONTRACT_VERSION = '1.0.1' as const

const nullableDate = z.string().refine((value) => Number.isFinite(Date.parse(value)), 'Invalid datetime').nullable()

const provenanceSchema = z.object({
  sourceSystem: z.string(),
  sourceTable: z.string().optional(),
  sourceRecordId: z.string().optional(),
  migrationStatus: z.string(),
  lastSyncedAt: nullableDate.optional(),
  readiness: z.string(),
  exceptionReason: z.string().optional(),
  canRefresh: z.boolean(),
  providerTouched: z.boolean(),
}).passthrough()

const profileSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string(),
  isAdmin: z.boolean(),
  sessionVersion: z.number().int().nullable(),
  status: z.string(),
  joinedAt: nullableDate,
  updatedAt: nullableDate,
  provenance: provenanceSchema,
}).passthrough()

const externalIdentitySchema = z.object({
  system: z.string(),
  externalId: z.string().nullable(),
  status: z.enum(['linked', 'not_connected', 'provider_gated']),
  provenance: provenanceSchema,
}).passthrough()

export const membershipMeProfileResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ME_READ_CONTRACT_VERSION),
  profile: profileSchema,
  externalIdentities: z.array(externalIdentitySchema),
}).passthrough()

const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.enum(['org-admin', 'org-member']),
  status: z.enum(['active', 'suspended', 'removed']),
  joinedAt: nullableDate,
  productKeys: z.array(z.string()),
  provenance: provenanceSchema,
}).passthrough()

const appAccessSchema = z.object({
  productKey: z.string(),
  label: z.string(),
  status: z.enum(['available', 'provider_gated', 'legacy_bridge', 'not_connected', 'not_built']),
  roleKeys: z.array(z.string()),
  orgIds: z.array(z.string()),
  href: z.string().nullable(),
  provenance: provenanceSchema,
}).passthrough()

const agentSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  serviceId: z.string().nullable(),
  status: z.string(),
  grantScope: z.enum(['user', 'organization']),
  orgId: z.string().nullable(),
  expiresAt: nullableDate,
  provenance: provenanceSchema,
}).passthrough()

const programSchema = z.object({
  id: z.string(),
  label: z.string(),
  orgId: z.string(),
  progressPercent: z.number().finite().nullable(),
  status: z.enum(['current', 'completed', 'not_started', 'needs_review']),
  startedAt: nullableDate,
  completedAt: nullableDate,
  provenance: provenanceSchema,
}).passthrough()

const reportSchema = z.object({
  id: z.string(),
  label: z.string(),
  productKey: z.string(),
  status: z.string(),
  createdAt: nullableDate,
  href: z.string().nullable(),
  provenance: provenanceSchema,
}).passthrough()

const planSchema = z.object({
  id: z.string(),
  label: z.string(),
  scope: z.enum(['individual', 'organization', 'solo_session', 'unknown']),
  status: z.enum(['active', 'trialing', 'past_due', 'provider_gated', 'not_connected', 'not_built']),
  renewsAt: nullableDate,
  provenance: provenanceSchema,
}).passthrough()

const integrationSchema = z.object({
  system: z.string(),
  label: z.string(),
  status: z.string(),
  externalId: z.string().nullable(),
  lastSyncedAt: nullableDate,
  provenance: provenanceSchema,
}).passthrough()

export const membershipMeDashboardSummaryResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ME_READ_CONTRACT_VERSION),
  profile: profileSchema,
  externalIdentities: z.array(externalIdentitySchema),
  generatedAt: z.string().datetime(),
  organizations: z.array(organizationSchema),
  apps: z.array(appAccessSchema),
  agents: z.array(agentSchema),
  programs: z.array(programSchema),
  reports: z.array(reportSchema),
  plans: z.array(planSchema),
  integrations: z.array(integrationSchema),
}).passthrough()

const membershipRecordSchema = z.object({
  id: z.number().int(),
  membershipType: z.string(),
  membershipName: z.string(),
  source: z.string(),
  status: z.string(),
  grantedAt: nullableDate,
  expiresAt: nullableDate,
  provenance: z.record(z.unknown()).nullable(),
}).passthrough()

const entitlementsSchema = z.object({
  promptAccess: z.enum(['no_access', 'partial', 'complete']),
  hubGroups: z.array(z.string()),
  thinkificPrograms: z.array(z.string()),
  pages: z.object({
    stateOfAiUpdates: z.boolean(),
    curatedAiTools: z.boolean(),
  }).passthrough(),
  communities: z.object({
    aiNewsUpdates: z.boolean(),
    generalFafoLounge: z.boolean(),
    generalImageFafo: z.boolean(),
    trainer: z.boolean(),
    cSuiteLounge: z.boolean(),
    aiInvesting: z.boolean(),
  }).passthrough(),
}).passthrough()

export const membershipMeMembershipsResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ME_READ_CONTRACT_VERSION),
  catalogAvailable: z.boolean(),
  memberships: z.array(membershipRecordSchema),
  entitlements: entitlementsSchema,
  generatedAt: z.string().datetime(),
}).passthrough()

const productGrantSchema = z.object({
  product: z.string(),
  roleKey: z.string(),
  roleDefinitionId: z.string(),
}).passthrough()

const personalMembershipSchema = z.object({
  userId: z.string(),
  status: z.enum(['active', 'suspended']),
  joinedAt: nullableDate,
}).passthrough()

const membershipContextSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('personal'),
    userId: z.string(),
    status: z.enum(['active', 'suspended']),
    joinedAt: nullableDate,
    is_default: z.boolean(),
    isDefault: z.boolean(),
  }).passthrough(),
  z.object({
    type: z.literal('org'),
    orgId: z.string(),
    orgName: z.string(),
    role: z.enum(['org-admin', 'org-member']),
    status: z.enum(['active', 'suspended', 'removed']),
    productGrants: z.array(productGrantSchema),
    joinedAt: nullableDate,
    is_default: z.boolean(),
    isDefault: z.boolean(),
  }).passthrough(),
])

const organizationMembershipSchema = z.object({
  orgId: z.string(),
  orgName: z.string(),
  role: z.enum(['org-admin', 'org-member']),
  status: z.enum(['active', 'suspended', 'removed']),
  productGrants: z.array(productGrantSchema),
  joinedAt: nullableDate,
}).passthrough()

const organizationAliasSchema = z.object({
  id: z.string(),
  org_id: z.string(),
  orgId: z.string(),
  name: z.string(),
  is_default: z.boolean(),
  isDefault: z.boolean(),
  is_active: z.boolean(),
  isActive: z.boolean(),
  active: z.boolean(),
}).passthrough()

export const membershipMeSelfMembershipResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ME_READ_CONTRACT_VERSION),
  org_id: z.string().optional(),
  orgId: z.string().optional(),
  default_org_id: z.string().optional(),
  defaultOrgId: z.string().optional(),
  has_personal_membership: z.boolean(),
  hasPersonalMembership: z.boolean(),
  personal_membership: personalMembershipSchema,
  personalMembership: personalMembershipSchema,
  membership_contexts: z.array(membershipContextSchema),
  membershipContexts: z.array(membershipContextSchema),
  products: z.array(z.string()).optional(),
  organizations: z.array(organizationAliasSchema).optional(),
  orgs: z.array(organizationAliasSchema).optional(),
  memberships: z.array(organizationMembershipSchema),
}).passthrough()

export type MembershipMeProfileResponse = z.infer<typeof membershipMeProfileResponseSchema>
export type MembershipMeDashboardSummaryResponse = z.infer<typeof membershipMeDashboardSummaryResponseSchema>
export type MembershipMeMembershipsResponse = z.infer<typeof membershipMeMembershipsResponseSchema>
export type MembershipMeSelfMembershipResponse = z.infer<typeof membershipMeSelfMembershipResponseSchema>
